import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Users, Trash2, Search, Edit3, Filter, X, ChevronUp, ChevronDown, UserPlus } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';

// shadcn/ui components
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '../components/ui/dialog';

interface Coach {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
  sex?: string;
  head_coach?: Coach;
  assistant_coaches?: Coach[];
  competition_level?: string;
}

interface AgeGroup {
  id: string;
  group_name: string;
  sex: string;
}

type SortField = 'name' | 'coach' | 'gender';
type SortDirection = 'asc' | 'desc';

interface EditTeamModalProps {
  team: Team | null;
  onClose: () => void;
  onTeamUpdated: (updatedTeam: Team) => void;
}

interface ManageCoachesModalProps {
  team: Team;
  onClose: () => void;
  onCoachesUpdated: (updatedTeam: Team) => void;
}

function EditTeamModal({ team, onClose, onTeamUpdated }: EditTeamModalProps) {
  const [name, setName] = useState(team?.name || '');
  const [description, setDescription] = useState(team?.description || '');
  const [sex, setSex] = useState(team?.sex || '');
  const [competitionLevel, setCompetitionLevel] = useState(team?.competition_level || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const competitionLevelOptions = ['Premier', 'Elite', 'Alliance'];

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (team) {
        // Update existing team
        const { error: updateError } = await supabase
          .from('teams')
          .update({
            name,
            description,
            sex
          })
          .eq('id', team.id);
        
        if (updateError) {
          throw updateError;
        }
        
        const updatedTeam: Team = {
          ...team,
          name,
          description,
          sex,
          competition_level: competitionLevel
        };
        
        onTeamUpdated(updatedTeam);
      } else {
        // Create new team
        const { data: newTeam, error: createError } = await supabase
          .from('teams')
          .insert({
            name,
            description,
            sex
          })
          .select();
        
        if (createError) {
          throw createError;
        }
        
        if (!newTeam || newTeam.length === 0) {
          throw new Error('Failed to create new team');
        }
        
        const createdTeam: Team = {
          ...newTeam[0],
          competition_level: competitionLevel,
          head_coach: null,
          assistant_coaches: []
        };
        
        onTeamUpdated(createdTeam);
      }
      
      onClose();
    } catch (err: any) {
      console.error('Error saving team:', err);
      setError(err.message || 'Failed to save team');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{team ? 'Edit Team' : 'Create Team'}</DialogTitle>
          <DialogDescription>{team ? 'Edit the team details below.' : 'Enter team details below.'}</DialogDescription>
        </DialogHeader>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-2">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
            <Select
              value={sex}
              onValueChange={(val) => setSex(val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Competition Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Competition Level</label>
            <Select
              value={competitionLevel}
              onValueChange={(val) => setCompetitionLevel(val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Competition Level" />
              </SelectTrigger>
              <SelectContent>
                {competitionLevelOptions.map(level => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : team ? 'Save' : 'Create Team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Modal for managing team coaches
function ManageCoachesModal({ team, onClose, onCoachesUpdated }: ManageCoachesModalProps) {
  const [headCoachId, setHeadCoachId] = useState<string>(team.head_coach?.id || 'none');
  const [assistantCoachIds, setAssistantCoachIds] = useState<string[]>(
    team.assistant_coaches?.map(coach => coach.id) || []
  );
  const [availableCoaches, setAvailableCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAvailableCoaches();
  }, []);

  const fetchAvailableCoaches = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('role', ['coach', 'administrator'])
        .order('first_name', { ascending: true })
        .order('last_name', { ascending: true });

      if (fetchError) throw fetchError;
      setAvailableCoaches(data || []);
    } catch (err: any) {
      console.error('Error fetching coaches:', err);
      setError(err.message || 'Failed to fetch coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      // First, remove all existing coach assignments for this team
      const { error: deleteError } = await supabase
        .from('team_coaches')
        .delete()
        .eq('team_id', team.id);

      if (deleteError) throw deleteError;

      // Add the head coach if one is selected
      if (headCoachId && headCoachId !== 'none') {
        const { error: headCoachError } = await supabase
          .from('team_coaches')
          .insert({
            team_id: team.id,
            coach_id: headCoachId,
            type: 'head'
          });

        if (headCoachError) throw headCoachError;
      }

      // Add assistant coaches
      if (assistantCoachIds.length > 0) {
        const assistantCoachesData = assistantCoachIds.map(coachId => ({
          team_id: team.id,
          coach_id: coachId,
          type: 'assistant'
        }));

        const { error: assistantCoachError } = await supabase
          .from('team_coaches')
          .insert(assistantCoachesData);

        if (assistantCoachError) throw assistantCoachError;
      }

      // Find head coach data (only if a real coach is selected, not 'none')
      const headCoach = (headCoachId && headCoachId !== 'none') ? 
        availableCoaches.find(coach => coach.id === headCoachId) : undefined;
      
      // Find assistant coaches data
      const assistantCoaches = availableCoaches.filter(coach => 
        assistantCoachIds.includes(coach.id)
      );

      // Update team object with new coach data
      const updatedTeam: Team = {
        ...team,
        head_coach: headCoach,
        assistant_coaches: assistantCoaches
      };

      onCoachesUpdated(updatedTeam);
      onClose();
    } catch (err: any) {
      console.error('Error updating coaches:', err);
      setError(err.message || 'Failed to update coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleAssistantCoachChange = (coachId: string, isSelected: boolean) => {
    if (isSelected) {
      // Add to assistant coaches if not already included
      if (!assistantCoachIds.includes(coachId)) {
        setAssistantCoachIds([...assistantCoachIds, coachId]);
      }
    } else {
      // Remove from assistant coaches
      setAssistantCoachIds(assistantCoachIds.filter(id => id !== coachId));
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Team Coaches</DialogTitle>
          <DialogDescription>Assign coaches to {team.name}.</DialogDescription>
        </DialogHeader>
        
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-2">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Head Coach</label>
            <Select 
              value={headCoachId}
              onValueChange={(value) => {
                setHeadCoachId(value);
                // Remove from assistant coaches if selected as head coach
                if (assistantCoachIds.includes(value)) {
                  setAssistantCoachIds(assistantCoachIds.filter(id => id !== value));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a head coach" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {availableCoaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.first_name} {coach.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assistant Coaches</label>
            {availableCoaches.length === 0 ? (
              <p className="text-sm text-gray-500">No coaches available</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {availableCoaches
                  .filter(coach => coach.id !== headCoachId) // Don't show head coach in assistant list
                  .map((coach) => (
                    <div key={coach.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`coach-${coach.id}`}
                        checked={assistantCoachIds.includes(coach.id)}
                        onChange={(e) => handleAssistantCoachChange(coach.id, e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor={`coach-${coach.id}`} className="text-sm text-gray-700">
                        {coach.first_name} {coach.last_name}
                      </label>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button 
            type="button" 
            onClick={handleSave} 
            disabled={loading}
            className="ml-2"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamsManagement() {
  const { } = useAuth();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [managingCoaches, setManagingCoaches] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Filter states
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>('all');
  const [competitionLevelFilter, setCompetitionLevelFilter] = useState<string>('all');
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);
  
  // Constants for filter options
  const competitionLevelOptions = ['Premier', 'Elite', 'Alliance'];

  const handleRemoveTeam = async (teamId: string) => {
    const confirmRemove = window.confirm(
      'Are you sure you want to remove this team? This cannot be undone.'
    );
    if (!confirmRemove) return;

    try {
      const { error: removeError } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);

      if (removeError) {
        throw removeError;
      }

      setTeams((prev) => prev.filter((t) => t.id !== teamId));
    } catch (err: any) {
      console.error('Error removing team:', err);
      setError(err.message || 'Failed to remove team');
    }
  };

  async function fetchTeams() {
    setLoadingTeams(true);
    setError('');

    try {
      // First, get all teams
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('*');

      if (teamsError) {
        throw teamsError;
      }

      // Extract competition level from team name (assuming format like "U12 Premier")
      const processedTeams = teamsData.map((team: any) => {
        let competitionLevel = '';
        if (team.name) {
          const nameParts = team.name.split(' ');
          const lastPart = nameParts[nameParts.length - 1];
          if (['Premier', 'Elite', 'Alliance'].includes(lastPart)) {
            competitionLevel = lastPart;
          }
        }
        return {
          ...team,
          competition_level: competitionLevel
        };
      });

      // Get all coaches for teams
      const teamIds = processedTeams.map((team: any) => team.id);
      const { data: coachesData, error: coachesError } = await supabase
        .from('team_coaches')
        .select(`
          team_id,
          coach_id,
          type,
          coach:coach_id(id, first_name, last_name)
        `)
        .in('team_id', teamIds);

      if (coachesError) {
        throw coachesError;
      }

      // Create maps for head coaches and assistant coaches
      const headCoachMap: {[key: string]: any} = {};
      const assistantCoachesMap: {[key: string]: any[]} = {};
      
      // Initialize assistant coaches array for each team
      teamIds.forEach(id => {
        assistantCoachesMap[id] = [];
      });
      
      // Populate the maps
      coachesData.forEach((coachData: any) => {
        if (coachData.type === 'head') {
          headCoachMap[coachData.team_id] = coachData.coach;
        } else if (coachData.type === 'assistant') {
          assistantCoachesMap[coachData.team_id].push(coachData.coach);
        }
      });

      // Merge coaches into team data
      const teamsWithCoaches = processedTeams.map((team: any) => ({
        ...team,
        head_coach: headCoachMap[team.id] || null,
        assistant_coaches: assistantCoachesMap[team.id] || []
      }));

      setTeams(teamsWithCoaches);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      setError(err.message || 'Failed to fetch teams');
    } finally {
      setLoadingTeams(false);
    }
  }

  // Function to handle team selection
  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
  };

  useEffect(() => {
    fetchTeams();
    fetchAgeGroups();
  }, []);

  // Fetch age groups from the database
  const fetchAgeGroups = async () => {
    setLoadingAgeGroups(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await supabase
        .from('age_groups')
        .select('*')
        .order('sex')
        .order('group_name');
        
      if (fetchError) {
        throw fetchError;
      }
      
      setAgeGroups(data || []);
    } catch (err: any) {
      console.error('Error fetching age groups:', err);
      setError(err.message || 'Failed to fetch age groups');
    } finally {
      setLoadingAgeGroups(false);
    }
  };

  // Filter teams based on search and filter criteria
  const filteredTeams = teams.filter((team) => {
    // Name search filter
    const matchesSearch = team.name.toLowerCase().includes(search.toLowerCase());
    
    // Gender filter
    const matchesGender = genderFilter === 'all' || team.sex === genderFilter;
    
    // Competition level filter
    const matchesCompetitionLevel = competitionLevelFilter === 'all' || team.competition_level === competitionLevelFilter;
    
    // Age group filter - Check if team matches the selected age group and sex
    let matchesAgeGroup = true;
    if (ageGroupFilter !== 'all') {
      const [ageGroupSex, ageGroupName] = ageGroupFilter.split(':');
      matchesAgeGroup = team.name.includes(ageGroupName) && team.sex === ageGroupSex;
    }
    
    return matchesSearch && matchesGender && matchesCompetitionLevel && matchesAgeGroup;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedTeams = () => {
    return [...filteredTeams].sort((a, b) => {
      // Apply appropriate sort logic based on the field
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
          
        case 'coach':
          const aCoach = a.head_coach ? `${a.head_coach.first_name || ''} ${a.head_coach.last_name || ''}`.trim() : '';
          const bCoach = b.head_coach ? `${b.head_coach.first_name || ''} ${b.head_coach.last_name || ''}`.trim() : '';
          comparison = aCoach.localeCompare(bCoach);
          break;
          
        case 'gender':
          const aGender = a.sex || '';
          const bGender = b.sex || '';
          comparison = aGender.localeCompare(bGender);
          break;
      }
      
      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleTeamUpdated = (updatedTeam: Team) => {
    setTeams((prev) => {
      // If team exists in the array, update it; otherwise add it
      const teamExists = prev.some(t => t.id === updatedTeam.id);
      if (teamExists) {
        return prev.map((t) => (t.id === updatedTeam.id ? updatedTeam : t));
      } else {
        return [...prev, updatedTeam];
      }
    });
  };
  
  const handleOpenCreateModal = () => {
    setIsCreatingTeam(true);
  };
  
  // Function to clear all filters
  const clearFilters = () => {
    setGenderFilter('all');
    setAgeGroupFilter('all');
    setCompetitionLevelFilter('all');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="mx-auto max-w-7xl py-10 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
          <div className="flex items-center space-x-2">
            <Users className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-bold tracking-tight text-gray-800">
              Manage Teams
            </h1>
          </div>
          <Button 
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-1"
          >
            Create Team
          </Button>
        </div>

        {error && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <section className="mt-8">
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow">
              <Search className="absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by team name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button 
              variant={showFilters ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
          
          {showFilters && (
            <div className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium">Filters</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear All
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Gender Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <Select 
                    value={genderFilter} 
                    onValueChange={setGenderFilter}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Genders" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Genders</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Age Group Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                  {loadingAgeGroups ? (
                    <div className="h-[38px] flex items-center justify-center bg-gray-50 border rounded-md">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                    </div>
                  ) : (
                    <Select 
                      value={ageGroupFilter} 
                      onValueChange={setAgeGroupFilter}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Age Groups" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Age Groups</SelectItem>
                        
                        {/* Group age groups by gender */}
                        {genderFilter === 'male' || genderFilter === 'all' ? (
                          <>
                            <SelectItem value="male-header" disabled className="font-bold">
                              Boys
                            </SelectItem>
                            {ageGroups
                              .filter(group => group.sex === 'male')
                              .map(group => (
                                <SelectItem key={group.id} value={`${group.sex}:${group.group_name}`}>
                                  Boys {group.group_name}
                                </SelectItem>
                              ))
                            }
                          </>
                        ) : null}
                        
                        {genderFilter === 'female' || genderFilter === 'all' ? (
                          <>
                            <SelectItem value="female-header" disabled className="font-bold mt-1">
                              Girls
                            </SelectItem>
                            {ageGroups
                              .filter(group => group.sex === 'female')
                              .map(group => (
                                <SelectItem key={group.id} value={`${group.sex}:${group.group_name}`}>
                                  Girls {group.group_name}
                                </SelectItem>
                              ))
                            }
                          </>
                        ) : null}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                {/* Competition Level Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Competition Level</label>
                  <Select 
                    value={competitionLevelFilter} 
                    onValueChange={setCompetitionLevelFilter}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      {competitionLevelOptions.map(level => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Team count indicator */}
          <div className="mb-4 text-sm text-gray-600 flex items-center justify-between">
            <div>
              {filteredTeams.length === teams.length ? (
                <span className="font-medium">{teams.length} Total Team{teams.length !== 1 ? 's' : ''}</span>
              ) : (
                <span>
                  <span className="font-medium">{filteredTeams.length}</span> of <span className="font-medium">{teams.length}</span> Team{teams.length !== 1 ? 's' : ''} (Filtered)
                </span>
              )}
            </div>
            {filteredTeams.length !== teams.length && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-xs hover:text-indigo-600 transition-colors"
              >
                Clear Filters
              </Button>
            )}
          </div>
          
          {loadingTeams ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredTeams.length === 0 ? (
            <p className="text-gray-600">No teams found.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Team Name
                        {sortField === 'name' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('coach')}
                    >
                      <div className="flex items-center">
                        Head Coach
                        {sortField === 'coach' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100" 
                      onClick={() => handleSort('gender')}
                    >
                      <div className="flex items-center">
                        Gender
                        {sortField === 'gender' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Competition Level</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getSortedTeams().map((team) => {
                    const coachName = team.head_coach
                      ? `${team.head_coach.first_name || ''} ${team.head_coach.last_name || ''}`.trim()
                      : '—';
                      
                    const isSelected = selectedTeam?.id === team.id;

                    return (
                      <TableRow 
                        key={team.id} 
                        className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-indigo-50' : ''}`}
                        onClick={() => handleTeamSelect(team)}
                      >
                        <TableCell className="whitespace-nowrap font-medium">{team.name}</TableCell>
                        <TableCell className="whitespace-nowrap">{coachName}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {team.sex ? 
                            team.sex.charAt(0).toUpperCase() + team.sex.slice(1).toLowerCase() 
                            : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{team.competition_level || '—'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingTeam(team)}
                              title="Edit Team"
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setManagingCoaches(team)}
                              title="Manage Coaches"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveTeam(team.id)}
                              title="Remove Team"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* Edit/Create Team Modal */}
        {(editingTeam || isCreatingTeam) && (
          <EditTeamModal
            team={editingTeam}
            onClose={() => {
              setEditingTeam(null);
              setIsCreatingTeam(false);
            }}
            onTeamUpdated={handleTeamUpdated}
          />
        )}
        
        {/* Manage Coaches Modal */}
        {managingCoaches && (
          <ManageCoachesModal
            team={managingCoaches}
            onClose={() => setManagingCoaches(null)}
            onCoachesUpdated={handleTeamUpdated}
          />
        )}
      </main>
    </div>
  );
}
